import { apiClient } from './apiClient';

export type FlashcardCardType = 'QuestionAnswer' | 'Cloze' | 'MultipleChoice';
export type FlashcardLibraryStatus = 'Active' | 'Hidden' | 'Removed';
export type FlashcardGenerationJobStatus = 'Pending' | 'Running' | 'Completed' | 'Failed';
export type FlashcardReportReason = 'Incorrect' | 'Misleading' | 'Offensive' | 'LowQuality' | 'Other';

// IMPORTANT: the backend serialises enums as INTEGERS by default, but ABP typically maps
// them to their enum member names in JSON responses via System.Text.Json's JsonStringEnumConverter.
// Check the shape of a response once in the browser and adjust these type aliases if the backend
// returns numbers (e.g. 1 | 2 | 3) instead of string names. If numeric, use numeric literal types:
//   export type FlashcardCardType = 1 | 2 | 3;
// and a small mapper in the UI. For v1, assume string-named enums (ABP default with
// JsonStringEnumConverter). If you see numbers in the JSON, switch.

export interface FlashcardLibraryCardDto {
  id: string;
  bookId: string;
  secureContentChunkId: string;
  chapterTitle: string | null;
  cardType: FlashcardCardType;
  question: string;
  answer: string;
  distractors: string[] | null;
  clozeText: string | null;
  generatedByProvider: string;
  pickCount: number;
  reportCount: number;
}

export interface RequestGenerationInput {
  bookId: string;
  secureContentChunkId: string;
  requestedCount: number;
}

export interface RequestGenerationResult {
  jobId: string;
  status: FlashcardGenerationJobStatus;
  existingJob: boolean;
}

export interface FlashcardGenerationJobDto {
  id: string;
  status: FlashcardGenerationJobStatus;
  cardsCreated: number;
  cardsRejectedAsDup: number;
  costUsd: number;
  errorMessage: string | null;
}

export interface AddLibraryCardToDeckInput {
  libraryCardId: string;
  deckId: string;
}

export interface ReportLibraryCardInput {
  libraryCardId: string;
  reason: FlashcardReportReason;
  notes?: string;
}

const BASE = '/api/app/flashcard-library-extn';

export const flashcardLibraryService = {
  async getByChapter(bookId: string, secureContentChunkId: string): Promise<FlashcardLibraryCardDto[]> {
    const response = await apiClient.get<FlashcardLibraryCardDto[]>(`${BASE}/by-chapter`, {
      params: { bookId, secureContentChunkId },
    });
    return response.data;
  },
  async requestGeneration(input: RequestGenerationInput): Promise<RequestGenerationResult> {
    const response = await apiClient.post<RequestGenerationResult>(`${BASE}/request-generation`, input);
    return response.data;
  },
  async getJob(jobId: string): Promise<FlashcardGenerationJobDto> {
    const response = await apiClient.get<FlashcardGenerationJobDto>(`${BASE}/jobs/${jobId}`);
    return response.data;
  },
  async addToDeck(input: AddLibraryCardToDeckInput): Promise<void> {
    await apiClient.post(`${BASE}/add-to-deck`, input);
  },
  async report(input: ReportLibraryCardInput): Promise<void> {
    await apiClient.post(`${BASE}/report`, input);
  },
};
